import { DatesSeparator, useToast } from '@koolumbus/web/ui';
import { createTxs, useLocale, useToggle } from '@koolumbus/web/utils';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React from 'react';
import { formatDate } from '../../../../lib/helpers/date';
import { PublicBookingWithAuctionBid } from '../../../../lib/models';
import { trpc } from '../../../../lib/trpc';
import AuctionBidActionsDialogs from '../../../auctions/AuctionBidActionsDialogs';
import FloatingCardContent from './FloatingCardContent';

interface AuctionBidContentProps {
  booking: PublicBookingWithAuctionBid & {
    auctionBid: NonNullable<PublicBookingWithAuctionBid['auctionBid']>;
  };
}

const AuctionBidContent: React.FC<React.PropsWithChildren<AuctionBidContentProps>> = ({
  booking,
}) => {
  const locale = useLocale();
  const trpcCtx = trpc.useUtils();
  const router = useRouter();
  const tx = useTxs();
  const toast = useToast();

  const [isAcceptBidConfirmOpen, { off: closeAcceptBid, on: openAcceptBid }] = useToggle();
  const [isRejectBidConfirmOpen, { off: closeRejectBid, on: openRejectBid }] = useToggle();

  const { mutateAsync: performReject } = trpc.auctions.rejectBid.useMutation({
    // invalidate: ['bookings/findByProperty'],
    onSuccess: () => {
      trpcCtx.bookings.list.invalidate();
      toast.info(tx.rejected);
      router.back();
    },
    onError: () => {
      toast.error();
    },
  });

  const onRejectBidConfirm = async () => {
    if (booking?.auctionBid.id) {
      await performReject({ id: booking.auctionBid.id });
      router.back();
    }
  };

  const { mutateAsync: performAccept } = trpc.auctions.close.useMutation({
    // invalidate: ['bookings/findByProperty'],
    onSuccess: () => {
      trpcCtx.bookings.list.invalidate();
    },
    onError: () => {
      toast.error();
    },
  });

  const onAcceptBidConfirm = async () => {
    if (booking?.auctionBid.id) {
      await performAccept({
        id: booking.auctionBid.auctionId,
        bidToAcceptId: booking.auctionBid.id,
      });
    }
  };

  const btnClass = 'w-full text-sm sm:text-base h-10 font-medium';

  return (
    <>
      <FloatingCardContent
        top={
          <>
            <p className={clsx('text-gray-500 text-lg', 'lg:text-xl')}>
              €{' '}
              <span className="text-gray-800 text-xl sm:text-2xl font-semibold sm:font-bold">
                {booking.auctionBid.price}
              </span>{' '}
              / {tx.month}
            </p>

            <div className="flex items-center text-gray-500 text-sm sm:text-base">
              <span>{formatDate(booking?.checkIn, locale)}</span>
              <div className="px-2">
                <DatesSeparator />
              </div>
              <span>{formatDate(booking?.checkOut, locale)}</span>
            </div>
          </>
        }
        center={
          <div className="flex flex-col w-full pl-2 space-y-1 text-gray-500">
            <span>
              {tx.deposit}: € {booking.deposit?.toLocaleString()}
            </span>
            <span>
              {tx.adminCosts}: € {booking.cleaningCost?.toLocaleString() ?? '-'}
            </span>
          </div>
        }
        bottom={
          <div className="flex w-full space-x-2">
            <button
              onClick={openRejectBid}
              aria-label="reject"
              className={clsx(
                'btn-base btn-lg',
                btnClass,
                'bg-red-200 hover:bg-red-300 text-red-600 rounded-md transition-colors focus:ring-red-500',
              )}
            >
              {tx.reject.label}
            </button>

            <button
              aria-label="accept"
              onClick={openAcceptBid}
              className={clsx(
                'btn-base btn-lg',
                btnClass,
                'bg-green-200 hover:bg-green-300 text-green-600 rounded-md transition-colors focus:ring-green-500',
              )}
            >
              {tx.accept.label}
            </button>
          </div>
        }
      />

      <AuctionBidActionsDialogs
        isAcceptOpen={isAcceptBidConfirmOpen}
        isRejectOpen={isRejectBidConfirmOpen}
        onCloseReject={closeRejectBid}
        onCloseAccept={closeAcceptBid}
        onRejectConfirm={onRejectBidConfirm}
        onAcceptConfirm={onAcceptBidConfirm}
      />
    </>
  );
};

const useTxs = createTxs({
  en: {
    month: 'month',
    showExtraCosts: 'Show extra costs',
    deposit: 'Deposit',
    adminCosts: 'Administrative costs',
    accept: {
      label: 'ACCEPT',
    },
    reject: {
      label: 'REJECT',
    },
    rejected: 'The offer has been rejected',
  },
  it: {
    month: 'mese',
    showExtraCosts: 'Mostra costi extra',
    deposit: 'Deposito',
    adminCosts: 'Costi amministrativi',
    accept: {
      label: 'ACCETTA',
    },
    reject: {
      label: 'RIFIUTA',
    },
    rejected: "L'offerta è stata rifiutata",
  },
});

export default AuctionBidContent;
